import * as React from "react";
import {
  Box,
  Chip,
  Button,
  Drawer,
  Typography,
  IconButton,
} from "@mui/material";
import { useLanguage } from "@tlc/ls2pac/components/Language";
import { useCallback, useMemo } from "react";
import { useReservesSearch } from "./ReservesSearchProvider";
import {
  ReserveOptions,
  ReservesInstructorOption,
  ReservesSearchForm,
} from "@tlc/ls2pac/components/ReservesSearchForm";
import { LocationSummary } from "@tlc/ls2pac/components/LocationDialog";
import { useBranchLimits } from "@tlc/ls2pac/components/BranchLimits";
import { FILTER_DRAWER_WIDTH, useShowFilterSidebar } from "../LSSearchResults";
import TuneIcon from "@mui/icons-material/Tune";

export function ReservesFilterDrawer({
  open,
  handleClose,
}: {
  open: boolean;
  handleClose: () => void;
}) {
  return (
    <Drawer anchor="left" open={open} onClose={handleClose}>
      <Box sx={{ p: 2 }} width={FILTER_DRAWER_WIDTH}>
        <ReservesSearchForm />
      </Box>
    </Drawer>
  );
}

interface ReservesSearchTokenDisplayProps {}

export const ReservesSearchTokenDisplay: React.FC<
  ReservesSearchTokenDisplayProps
> = () => {
  const {
    searchParams: { department, course, instructor, branchFilters },
    setDepartment,
    setCourse,
    setInstructor,
    clearReservesParams,
    setBranchFilters,
    allOptions,
    hasActiveFilters,
  } = useReservesSearch();

  const { branchLimits, selectedBranchIds, updateSelectedBranches } =
    useBranchLimits();

  const displayBranchIds =
    branchFilters.length > 0 ? branchFilters : selectedBranchIds;

  const { translate } = useLanguage();
  const showFilterSidebar = useShowFilterSidebar();
  const [open, setOpen] = React.useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  React.useEffect(() => {
    if (showFilterSidebar) {
      setOpen(false);
    }
  }, [showFilterSidebar]);

  const getName = useCallback(
    (key: keyof ReserveOptions, value: string | null) => {
      if (!allOptions) return value;
      const option = (allOptions as ReserveOptions)[key].find(
        (o) => o.value === value
      );
      if (key === "instructors") {
        return (
          (option as ReservesInstructorOption)?.displayName ||
          option?.name ||
          value
        );
      }
      return option?.name || value;
    },
    [allOptions]
  );

  const departmentName = useMemo(
    () => getName("departments", department),
    [department, getName]
  );
  const courseName = useMemo(
    () => getName("courses", course),
    [course, getName]
  );
  const instructorName = useMemo(
    () => getName("instructors", instructor),
    [instructor, getName]
  );

  const handleClearLocations = () => {
    setBranchFilters([]);
    updateSelectedBranches([]);
  };

  if (!hasActiveFilters && showFilterSidebar) {
    return null;
  }

  if (!hasActiveFilters && !showFilterSidebar) {
    return (
      <>
        <Button
          color="primary"
          startIcon={<TuneIcon />}
          onClick={handleOpen}
          aria-haspopup="dialog"
          aria-expanded={open ? "true" : "false"}
          sx={{ flexShrink: 0, minWidth: "auto", width: "auto" }}
        >
          {translate("filter")}
        </Button>
        <ReservesFilterDrawer open={open} handleClose={handleClose} />
      </>
    );
  }

  const chipStyles = {
    maxWidth: "320px", //Some of these chip values can be quite long
    textOverflow: "ellipsis",
    overflow: "hidden",
  };

  return (
    <Box
      gap={1}
      flex={1}
      sx={{ display: "flex" }}
      alignItems={"center"}
      flexWrap={"wrap"}
    >
      {!showFilterSidebar ? (
        <>
          <ReservesFilterDrawer open={open} handleClose={handleClose} />
          <IconButton
            onClick={handleOpen}
            color="primary"
            aria-label={translate("filter")}
          >
            <TuneIcon />
          </IconButton>
        </>
      ) : (
        <Typography sx={{ whiteSpace: "nowrap" }}>
          {translate("filteredBy")}
        </Typography>
      )}

      {displayBranchIds && displayBranchIds.length > 0 && (
        <Chip
          label={
            <LocationSummary
              branchLimits={branchLimits}
              selected={displayBranchIds}
            />
          }
          onDelete={handleClearLocations}
          color="secondary"
          variant="outlined"
          sx={chipStyles}
        />
      )}
      {!!department && (
        <Chip
          label={`${translate("department")}: ${departmentName}`}
          onDelete={() => setDepartment("")}
          color="secondary"
          variant="outlined"
          sx={chipStyles}
        />
      )}
      {!!course && (
        <Chip
          label={`${translate("course")}: ${courseName}`}
          onDelete={() => setCourse("")}
          color="secondary"
          variant="outlined"
          sx={chipStyles}
        />
      )}
      {!!instructor && (
        <Chip
          label={`${translate("instructor")}: ${instructorName}`}
          onDelete={() => setInstructor("")}
          color="secondary"
          variant="outlined"
          sx={chipStyles}
        />
      )}
      <Button size="small" onClick={clearReservesParams}>
        {translate("clear")}
      </Button>
    </Box>
  );
};
